/**
 * Blinkit-style fly-to-cart animation.
 * Clones the product image (or the source element) and animates it along
 * a small curved path into the cart icon. Completes in <500 ms.
 *
 * Usage: flyToCart({ sourceEl, imgUrl })
 *   - sourceEl : the DOM node that acted as ADD (button/card). Required.
 *   - imgUrl   : product thumbnail. If missing we fly a red dot instead.
 *
 * Zero deps, pure DOM. Respects prefers-reduced-motion.
 */
export function flyToCart({ sourceEl, imgUrl } = {}) {
  if (!sourceEl || typeof document === "undefined") return;
  if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;

  const target =
    document.querySelector('[data-testid="pdp-view-cart-pill"]') ||
    document.querySelector('[data-testid="bottom-nav-cart"]') ||
    document.querySelector('[data-testid="header-cart-btn"]') ||
    document.querySelector('[data-testid="mini-cart"]');
  if (!target) return;

  const src = sourceEl.getBoundingClientRect();
  const tgt = target.getBoundingClientRect();

  const size = Math.min(56, Math.max(36, Math.min(src.width, src.height) || 44));
  const sx = src.left + src.width / 2 - size / 2;
  const sy = src.top + src.height / 2 - size / 2;
  const tx = tgt.left + tgt.width / 2 - size / 2;
  const ty = tgt.top + tgt.height / 2 - size / 2;
  const dx = tx - sx;
  const dy = ty - sy;

  const el = document.createElement("div");
  el.setAttribute("data-fly-clone", "1");
  el.style.cssText = [
    "position:fixed",
    `left:${sx}px`,
    `top:${sy}px`,
    `width:${size}px`,
    `height:${size}px`,
    "border-radius:14px",
    "background:white",
    "box-shadow:0 12px 28px -8px rgba(0,0,0,0.35)",
    "z-index:99999",
    "pointer-events:none",
    "overflow:hidden",
    "will-change:transform,opacity",
    "transition:transform 460ms cubic-bezier(0.55,0.05,0.4,0.95),opacity 460ms ease-in",
    "display:grid;place-items:center",
  ].join(";");

  if (imgUrl) {
    const img = document.createElement("img");
    img.src = imgUrl;
    img.alt = "";
    img.style.cssText = "width:100%;height:100%;object-fit:contain;padding:4px";
    el.appendChild(img);
  } else {
    el.style.background = "hsl(0 84% 46%)";
    el.innerHTML = '<span style="color:#fff;font-weight:800;font-size:14px">+1</span>';
  }

  document.body.appendChild(el);

  // Two-phase transform for a stronger curve: first big lift + rotate, then dive.
  requestAnimationFrame(() => {
    el.style.transform = `translate(${dx * 0.45}px, ${dy * 0.25 - 80}px) scale(0.8) rotate(-14deg)`;
  });
  setTimeout(() => {
    el.style.transform = `translate(${dx}px, ${dy}px) scale(0.14) rotate(18deg)`;
    el.style.opacity = "0.15";
  }, 180);

  // Ping the target so users see a satisfying bounce when the item "lands".
  setTimeout(() => {
    target.animate(
      [
        { transform: "scale(1)" },
        { transform: "scale(1.25)" },
        { transform: "scale(0.95)" },
        { transform: "scale(1)" },
      ],
      { duration: 320, easing: "cubic-bezier(0.34, 1.56, 0.64, 1)" },
    );
  }, 410);

  setTimeout(() => el.remove(), 520);
}
